/**
 * Authentication service for FixMatePro
 * 
 * This service handles authentication token management for API calls
 */

import { getAuth } from 'firebase/auth';

/**
 * Get the current authentication token from Firebase
 * 
 * @returns {Promise<string>} The authentication token
 */
export const getAuthToken = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  try {
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw error;
  }
};

/**
 * Check if the current user has admin role
 * 
 * @returns {Promise<boolean>} True if the user is an admin
 */
export const isAdmin = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    return false;
  }
  
  try {
    const token = await user.getIdTokenResult();
    return token.claims.admin === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Get the current user's ID
 * 
 * @returns {string|null} The user ID or null if not authenticated
 */
export const getCurrentUserId = () => {
  const auth = getAuth();
  return auth.currentUser?.uid || null;
};

/**
 * Check if the user is authenticated
 * 
 * @returns {boolean} True if the user is authenticated
 */
export const isAuthenticated = () => {
  const auth = getAuth();
  return !!auth.currentUser;
};
